import React from "react";
import { IFeedbackJob } from "../types";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";

interface FeedbackPopupProps {
  onClose: () => void;
  jobs: IFeedbackJob[];
  onProvideFeedback: (job: IFeedbackJob) => void;
}

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({
  onClose,
  jobs,
  onProvideFeedback,
}) => {
     const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
       if (e.target === e.currentTarget) {
         onClose();
       }
     };

     const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
       e.stopPropagation();
     };
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={handleModalClick}
      >
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Offer Feedback Required
            </h2>
            <p className="text-gray-600">
              Help us improve by sharing your recruitment outcomes
            </p>
          </div>

          {/* Summary Card */}
          <div className="bg-orange-50 border border-blue-100 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">
                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-white text-sm"></i>
                </div>
              </div>
              <div className="ml-3">
                <p className="font-semibold text-red-900">
                  You have {jobs.length} closed job
                  {jobs.length !== 1 ? "s" : ""} awaiting feedback
                </p>
                <p className="text-red-700 text-sm mt-1">
                  Providing feedback helps us understand successful placements
                  and improve our platform for everyone.
                </p>
              </div>
            </div>
          </div>

          {/* Jobs List */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Jobs Awaiting Feedback:
            </h3>

            {jobs.length === 0 ? (
              <div className="text-center py-6">
                <i className="ri-check-double-line text-4xl text-green-500 mb-3"></i>
                <p className="text-gray-600">All feedback has been provided!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {jobs.map((job, index) => (
                  <div
                    key={job.id || index}
                    className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {job.title}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {job.company_name}
                        </p>
                      </div>
                      <button
                        onClick={() => onProvideFeedback(job)}
                        className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Provide Feedback
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Posted:</span>
                        <span className="ml-2 font-medium">
                          {formatDate(job.created_at)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Applications:</span>
                        <span className="ml-2 font-medium">
                          {job.applications_count || "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Closed:</span>
                        <span className="ml-2 font-medium">
                          {formatDate(job.closed_at || job.updated_at)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Experience:</span>
                        <span className="ml-2 font-medium capitalize">
                          {job.level?.replace(/_/g, " ")}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Why Feedback Matters */}
          <div className="mb-8 bg-blue-100 p-3">
            <h4 className="text-lg font-semibold text-blue-900 mb-3">
              Why your feedback matters:
            </h4>
            <ul className="space-y-1">
              <li className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                </div>
                <span className="ml-3 text-blue-700">
                  Track successful hires and measure your recruitment performance.
                </span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                </div>
                <span className="ml-3 text-blue-700">
                  Ensure your hiring outcomes shape how the platform works for you
                </span>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                </div>
                <span className="ml-3 text-blue-700">
                  Help us tailor the platform to better support your hiring needs.
                </span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Remind me later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPopup;
